<template>
  <div class="gw-stats-widget">
    <!-- Not using v-if cause we use the component to manage range dates -->
    <ChartPeriodSelector
      v-show="displayRangeSelector"
      ref="periodSelector"
      v-model="displayConfig"
      :default-expanded="false"
      :text="$t('message.GW_STATS')"
      :expandable="false"
      :preset-id="presetId"
      :query-prefix="`indicatorWidget${widget.id}`"
      @presetPick="presetId = $event"
      @presetEditorOpened="setMaxZIndex(true)"
      @presetEditorClosed="setMaxZIndex(false)"
    />
    <GwPlanningCompliance
      v-if="whichStat === 0"
      ref="gwStatChart"
      :type="selectedType"
      :teams="gwStatsOptTeamFilter"
      :templates="gwStatsOptTemplateFilter"
      :range="visibleValuesRange"
      :working-days="allTeamsWorkingDays"
      :hide-non-working-days="gwStatsHideNonWD === 1"
      :display-config="displayConfig"
    />
    <GwChecklistItemsChart
      v-else-if="whichStat === 1"
      v-show="!resizing"
      ref="gwStatChart"
      :style="chartStyle"
      :type="selectedType"
      :stats-data="statsData"
      :cl-items="clItems"
      :working-days="allTeamsWorkingDays"
      :hide-non-working-days="gwStatsHideNonWD === 1"
      :visible-chart-range="visibleValuesRange"
      :display-config="displayConfig"
    />
    <GwNOKItemsChart
      v-else-if="whichStat === 2"
      v-show="!resizing"
      ref="gwStatChart"
      :gw-data="gwData"
      :style="chartStyle"
    />
    <GwNOKItemsChart
      v-else-if="whichStat === 3"
      v-show="!resizing"
      ref="gwStatChart"
      :gw-data="gwData"
      :style="chartStyle"
      :group-by="1"
    />
    <GwNOKItemsChart
      v-else
      v-show="!resizing"
      ref="gwStatChart"
      :gw-data="gwData"
      :style="chartStyle"
      :group-by="2"
    />
  </div>
</template>

<script>
import toastr from 'toastr';
import isEqual from 'lodash/isEqual';
// Vuex
import { mapState } from 'vuex';
import { FETCH_TEAMS } from '@/store/action-types';
import { GET_TEAMS } from '@/store/mutation-types';
// Import components
import ChartPeriodSelector from '@/components/chart-period-selector.vue';
import GwChecklistItemsChart from '@/components/gw-checklist-items-chart.vue';
import GwNOKItemsChart from '@/components/gw-nok-items-chart.vue';
import GwPlanningCompliance from '@/components/gw-planning-compliance.vue';
// Import mixins
import mixinSocketHandler from '@/mixins/socket-handler.vue';
import mixinWidgets from '@/mixins/widgets.vue';
import mixinWidgetsBgColor from '@/mixins/widgets-bgcolor-config.vue';
import mixinGwStatsOptions from '@/mixins/page-options/gw-stats.vue';
// Import utils
import SocketMessages from '@/utils/classes/SocketMessages';
import { CL_ITEMS_CHART_TYPES, AVAILABLE_STATS } from '@/utils/gw-stats';
import { nestedFindItemWithId, treeAsArray, calcPeriodStartDate, parseWorkingDays } from '@/utils/main';
import { computeConfigStart } from '@/utils/indicator-utils';


export default {

  name: 'GwStatsWidget',

  components: {
    ChartPeriodSelector,
    GwChecklistItemsChart,
    GwNOKItemsChart,
    GwPlanningCompliance,
  },

  mixins: [mixinWidgets, mixinWidgetsBgColor, mixinGwStatsOptions, mixinSocketHandler],

  props: {
    whichStat: {
      type: Number,
      default: 0,
      validator: v => !!AVAILABLE_STATS[v],
    },
    typeId: {
      type: Number,
      default: 0,
      validator: v => !!CL_ITEMS_CHART_TYPES[v],
    },
    displayRangeSelector: Boolean,
    defaultPeriod: {
      type: Number,
      default: 5,
    },
    defaultDaysGap: {
      type: Number,
      default: 0,
    },
    hideNonWD: {
      type: Number,
      default: 0,
    },
    clItems: {
      type: Array,
      default: () => [0, 1, 2],
    },
    zoneAll: {
      type: Number,
      default: 0,
    },
    zoneFilter: {
      type: Number,
      default: 0,
      validator: v => v >= 0,
    },
    teamAll: {
      type: Number,
      default: 0,
    },
    teamFilter: {
      type: Array,
      default: () => [],
    },
    checklistAll: {
      type: Number,
      default: 0,
    },
    checklistFilter: {
      type: Array,
      default: () => [],
    },
    templateAll: {
      type: Number,
      default: 0,
    },
    templateFilter: {
      type: Array,
      default: () => [],
    },
  },

  data() {
    return {
      statsData: {},
      gwData: [],
      displayConfig: this.$store.getters.indicDisplayConfigs.find(config => config.id === 5),
    };
  },

  computed: {
    config() {
      // Hide all settings related to the team when editing a config template
      let keysToHide = [];
      if (this.cfgTemplateEdition) {
        keysToHide = [
          'zoneTitle', 'zoneAll', 'zoneFilter',
          'teamTitle', 'teamAll', 'teamFilter',
          'checklistTitle', 'checklistAll', 'checklistFilter',
          'templateTitle', 'templateAll', 'templateFilter',
        ];
      }
      return [
        {
          key: 'gwStatTopSeparator',
          value: '',
          component: 'Div',
          options: { class: 'filters-zone__modal__sep' },
        },
        {
          key: 'whichStat',
          component: 'EditableLabelRadio',
          value: this.whichStat,
          options: {
            class: 'mt5 mb5',
            legend: this.$t('message.DISPLAY'),
            items: AVAILABLE_STATS.map(t => ({ ...t, label: this.$t(t.i18nLabel) })),
            labelKey: 'label',
            genericBorder: true,
          },
        },
        {
          key: 'typeId',
          component: 'EditableLabelRadio',
          value: this.typeId,
          options: {
            class: 'generic-field-margin',
            legend: this.$t('message.CHART_TYPE'),
            items: CL_ITEMS_CHART_TYPES.map((t, idx) => ({ id: idx, label: this.$t(t.i18nLabel) })),
            labelKey: 'label',
            genericBorder: true,
          },
        },
        {
          key: 'displayRangeSelector',
          value: this.displayRangeSelector,
          component: 'Checkbox',
          options: {
            title: this.$t('message.WIDGET_INDIC_DISPLAY_RANGE'),
          },
        },
        ...this.gwStatsOptions.filter(opt => !keysToHide.includes(opt.key)),
        this.defaultBgColorConfig,
      ];
    },
    chartStyle() {
      let height = this.displayRangeSelector ? 'calc(100% - 60px)' : '100%';
      if (this.whichStat === 0) {
        height = this.displayRangeSelector ? 'calc(100% - calc(60px + 2.5em))' : '100%';
      }
      return { height };
    },
    selectedType() {
      return CL_ITEMS_CHART_TYPES[this.typeId].type;
    },
    /** Teams from vuex */
    allTeams() {
      return this.$store.state[GET_TEAMS.stateKey] || [];
    },
    ...mapState({
      allTeams: state => state[GET_TEAMS.stateKey] || [],
      allTeamsLoading: state => state[GET_TEAMS.loadingKey],
    }),
    /** Merge all working days of fetched teams */
    allTeamsWorkingDays() {
      const teams = this.gwStatsOptTeamAll === 0
        ? this.allTeams
        : this.allTeams.filter(team => this.gwStatsOptTeamFilter.includes(team.id));
      const allWorkingDays = teams.reduce((acc, team) => acc.concat(team.workingDays), []);
      return parseWorkingDays(allWorkingDays.filter((v, idx, a) => a.indexOf(v) === idx));
    },
    fetchParams() {
      return {
        completed: true,
        includeData: false,
        range: {
          start: this.allValuesRange.start.format('YYYY-MM-DD HH:mm:ss'),
          end: this.allValuesRange.end.format('YYYY-MM-DD HH:mm:ss'),
        },
        zones: this.zoneAll === 1 ? this.getZones(this.zoneFilter) : undefined,
        teams: this.teamAll === 1 ? this.teamFilter : undefined,
        checklists: this.checklistAll === 1 ? this.checklistFilter : undefined,
        templates: (this.templateAll === 1 && this.templateFilter.length > 0
          ? this.templateFilter
          : undefined),
      };
    },
    /** Object: Range of chart's visible values */
    visibleValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      const { chartPeriodObj, pointPeriodObj, chartEnd } = this.displayConfig;
      return {
        start: calcPeriodStartDate(chartEnd, chartPeriodObj, pointPeriodObj),
        end: chartEnd.clone().endOf('day'),
      };
    },
    /**
     * Object: Range of all values, including values not displayed.
     * Especially useful when building a cumulated chart (where increasingValues > 0).
     * eg: Values should be cumulated each month from the 1st of each month
     *     => need the values from the 1st day of month
     */
    allValuesRange() {
      if (!this.displayConfig) {
        return null;
      }
      if (!this.displayConfig.increasingValues) {
        // No increasing values, no need for an extended range
        return this.visibleValuesRange;
      }
      const { chartEnd, configStart } = computeConfigStart(this.displayConfig, this.visibleValuesRange.start);
      return { start: this.$moment(configStart), end: chartEnd };
    },
    /** Indicator config ID */
    presetId: {
      get() {
        const { presetId } = this.queryParams;
        return presetId === null || isNaN(presetId) ? this.defaultPeriod : Number(presetId);
      },
      set(presetId) {
        // Preset picked from the widget => update query for meeting sync
        this.setQueryParams({ presetId });
      },
    },
    // mixinGwStatsOptions overrides
    componentsListRefs() {
      return this.configPanel.$refs.componentsList.$refs;
    },
    gwStatsHideNonWD() {
      return this.hideNonWD;
    },
    gwStatsNonWDNumber() {
      return this.allTeamsWorkingDays.filter(v => !v).length;
    },
    gwStatsOptClItems() {
      return this.clItems;
    },
    gwStatsOptZoneAll() {
      return this.zoneAll;
    },
    gwStatsOptZoneFilter() {
      return this.zoneFilter;
    },
    gwStatsOptTeamAll() {
      return this.teamAll;
    },
    gwStatsOptTeamFilter() {
      if (this.teamAll === 0) {
        return this.$store.getters.teamsTreeCurrentFlattened.map(team => team.id);
      }
      return this.teamFilter;
    },
    gwStatsOptChecklistAll() {
      return this.checklistAll;
    },
    gwStatsOptChecklistFilter() {
      return this.checklistFilter;
    },
    gwStatsOptTemplateAll() {
      return this.templateAll;
    },
    gwStatsOptTemplateFilter() {
      return this.templateFilter;
    },
    gwStatsOptDefaultPresetId() {
      return this.defaultPeriod;
    },
  },

  watch: {
    fetchParams(newVal, oldVal) {
      if (!isEqual(newVal, oldVal)) {
        this.fetchStats();
      }
    },
    whichStat() {
      this.fetchStats();
    },
    displayRangeSelector() {
      this.resize();
    },
    range: {
      handler(newValue) {
        const { rpt } = this.$refs;
        if (!rpt) return;
        this.$refs.rpt.endDate = newValue.end;
        this.$refs.rpt.rangeIndex = newValue.index;
        this.$refs.rpt.resetStartDate();
      },
      deep: true,
    },
  },

  created() {
    this.fetchStats();
    this.handleSocketMessage(SocketMessages.CC_MSGS.GW_END, this.fetchStats);

    if (!this.allTeamsLoading && !this.cfgTemplateEdition) {
      this.$store.dispatch(FETCH_TEAMS);
    }
  },

  methods: {
    resize() {
      this.$nextTick(() => {
        this.$refs.gwStatChart.resizeChart();
      });
    },
    /**
     * Get all childs of a given zone id
     * Taken from views/gw/stats.vuE
     * @param {Number} zoneId - ID of a zone
     * @return {Number[]} List of zone ids, zoneId included
     */
    getZones(zoneId) {
      const zone = nestedFindItemWithId(this.gwStatsOptZonesData, zoneId, 'zones');
      return zone ? [zoneId, ...treeAsArray(zone.zones, 'zones').map(z => z.id)] : [];
    },
    /**
     * Fetch and store GW stats data
     * Parameters are set with the computed property `fetchParams`
     */
    fetchStats() {
      if (this.cfgTemplateEdition) {
        return;
      }
      if (this.whichStat === 1) {
        this.App.post('/server/gembaWalks/getStats.php', this.fetchParams, (data) => {
          if (!(data instanceof Object)) {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
            return;
          }
          this.statsData = data.data;
        });
      } else {
        this.App.post('/server/gembaWalks/get.php', this.fetchParams, (data) => {
          if (!(data instanceof Array)) {
            toastr.error(this.$t('message.CONNECTION_ERROR'));
            return;
          }
          this.gwData = data;
        });
      }
    },
  },
};
</script>

<style lang="scss">
.gw-stats-widget {
  overflow: auto;

  .gw-planning-compliance {
    padding-top: 2.5em;

    &__toggle-chart.add-button.reverse {
      top: 0px;
    }

    &__table-wrapper {
      padding-top: 0px;
    }
  }
}
</style>
